import { useTranslation } from 'react-i18next';
import { ChatContextType, VisualStyleData, VisualStyleAIResponse } from '@packages/global';
import BaseStepChatPanel, { ActionCard } from './BaseStepChatPanel';

export interface VisualStyleChatPanelProps {
  projectId: string;
  episodeScriptId: string;
  currentData?: VisualStyleData | null;
  onApply?: (data: Partial<VisualStyleData>) => void;
}

export default function VisualStyleChatPanel({ projectId, episodeScriptId, currentData, onApply }: VisualStyleChatPanelProps) {
  const { t } = useTranslation('script');
  const parseResponse = (content: string): { message: string; actions: ActionCard[] } => {
    try {
      let parsed: VisualStyleAIResponse | null = null;

      // 1. 尝试匹配 markdown 代码块中的 JSON
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch) {
        parsed = JSON.parse(jsonMatch[1]);
      }

      // 2. 尝试直接解析为 JSON
      if (!parsed) {
        const trimmed = content.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          parsed = JSON.parse(trimmed);
        }
      }

      // 3. 尝试提取 JSON 对象
      if (!parsed) {
        const jsonObjectMatch = content.match(/\{[\s\S]*"message"[\s\S]*\}/);
        if (jsonObjectMatch) {
          parsed = JSON.parse(jsonObjectMatch[0]);
        }
      }

      if (!parsed) return { message: content, actions: [] };

      const actions: ActionCard[] = [];
      if (parsed.content) {
        actions.push({
          type: 'update',
          title: t('step.visualStyle'),
          content: `${t('visualStyle.name')}: ${parsed.content.styleName || ''}\n${t('visualStyle.description')}: ${parsed.content.artStyle || ''}`,
          data: parsed.content
        });
      }
      return { message: parsed.message || '', actions };
    } catch {
      return { message: content, actions: [] };
    }
  };

  const handleApplyAction = (action: ActionCard) => {
    if (action.data && onApply) {
      onApply(action.data as Partial<VisualStyleData>);
    }
  };

  return (
    <BaseStepChatPanel
      projectId={projectId}
      episodeScriptId={episodeScriptId}
      stepNumber={2}
      stepName={t('step.visualStyle')}
      contextType={ChatContextType.SCRIPT_VISUAL_STYLE}
      currentStepData={currentData}
      onApplyAction={handleApplyAction}
      parseResponse={parseResponse}
    />
  );
}
